
import { useState, useEffect, useRef } from "react";
import { useStore } from "@/store/useStore";
import { useAuthContext } from "@/contexts/auth";
import { useToast } from "@/hooks/use-toast";
import { AlertsListContainer } from "./price-alerts-list";
import { PriceAlert } from "@/types/price";
import { logAuthState } from "@/utils/authValidation";

export function PriceAlertsList() {
  const { 
    priceAlerts, 
    togglePriceAlert, 
    deletePriceAlert, 
    loadUserPriceAlerts,
    currentGoldPrice, 
    currentSilverPrice 
  } = useStore();
  const { user, isAuthenticated } = useAuthContext();
  const [userAlerts, setUserAlerts] = useState<PriceAlert[]>([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const { toast } = useToast();
  const mountedRef = useRef(false);
  const alertsLoadedRef = useRef(false);

  // Debug dello stato utente al mount
  useEffect(() => {
    logAuthState(isAuthenticated, user, 'PriceAlertsList', {
      alertsCount: priceAlerts.length
    });
    
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
    };
  }, [user, priceAlerts, isAuthenticated]);

  // Carica avvisi dal database quando l'utente si autentica
  useEffect(() => {
    const loadAlertsFromDatabase = async () => {
      if (isAuthenticated && user?.id && !alertsLoadedRef.current) {
        console.log('[PriceAlertsList] Loading alerts from database for user:', user.id);
        try {
          await loadUserPriceAlerts(user.id);
          alertsLoadedRef.current = true;
        } catch (error) {
          console.error('[PriceAlertsList] Error loading alerts from database:', error);
        }
      }
    };

    loadAlertsFromDatabase();
  }, [isAuthenticated, user?.id, loadUserPriceAlerts]);

  // Reset flag quando l'utente si disconnette
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      alertsLoadedRef.current = false;
    }
  }, [isAuthenticated, user?.id]);

  // Filtro avvisi quando cambia lo stato di auth o gli avvisi
  useEffect(() => {
    console.log("[PriceAlertsList] Filtraggio avvisi - Stato utente:", { 
      userAuthenticated: isAuthenticated,
      userId: user?.id,
      alertsCount: priceAlerts.length
    });
    
    setIsFiltering(true);
    
    try {
      if (isAuthenticated && user?.id) {
        // Se autenticato, mostra solo gli avvisi dell'utente
        const filteredAlerts = priceAlerts.filter(alert => alert.userId === user.id);
        console.log(`[PriceAlertsList] Trovati ${filteredAlerts.length} avvisi per l'utente ${user.id}`);
        setUserAlerts(filteredAlerts);
      } else {
        // Se non autenticato, mostra avvisi senza userId (avvisi locali)
        const filteredAlerts = priceAlerts.filter(alert => !alert.userId);
        console.log(`[PriceAlertsList] Trovati ${filteredAlerts.length} avvisi locali per utente non autenticato`);
        setUserAlerts(filteredAlerts);
      }
    } catch (error) {
      console.error('[PriceAlertsList] Errore nel filtraggio avvisi:', error);
    } finally {
      setIsFiltering(false);
    }
  }, [user, priceAlerts, isAuthenticated]);

  const handleToggle = async (alertId: string) => {
    try {
      console.log("[PriceAlertsList] Toggle avviso:", alertId);
      await togglePriceAlert(alertId);
      toast({
        title: "Avviso aggiornato",
        description: "Lo stato dell'avviso è stato modificato con successo"
      });
    } catch (error) {
      console.error("[PriceAlertsList] Errore toggle:", error);
      toast({
        title: "Errore",
        description: "Impossibile aggiornare l'avviso di prezzo",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (alertId: string) => {
    try {
      console.log("[PriceAlertsList] Eliminazione avviso:", alertId);
      await deletePriceAlert(alertId);
      toast({
        title: "Avviso eliminato",
        description: "L'avviso di prezzo è stato eliminato con successo"
      });
    } catch (error) {
      console.error("[PriceAlertsList] Errore eliminazione:", error);
      toast({
        title: "Errore",
        description: "Impossibile eliminare l'avviso di prezzo",
        variant: "destructive",
      });
    }
  };

  return (
    <AlertsListContainer
      isAuthenticated={isAuthenticated && !!user}
      userAlerts={userAlerts}
      isLoading={isFiltering}
      currentGoldPrice={currentGoldPrice}
      currentSilverPrice={currentSilverPrice}
      onToggle={handleToggle}
      onDelete={handleDelete}
    />
  );
}
